import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import ReportWidget from './components/ReportWidget';
import { fetchInventoryReport } from '../../store/reportingInventory/actions';
import { formatCurrency as formatCurrencyBase } from '../../helpers/formatCurrency';
import useOrgCurrency from '../../Hooks/useOrgCurrency';

const TOP_N = 6;

const formatMoney = (value, currency) =>
  formatCurrencyBase(value, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const formatCompactMoney = (value, currency, locale) =>
  new Intl.NumberFormat(locale || 'en-US', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value) || 0);

const sumAmount = (summaries) =>
  (summaries || []).reduce((sum, s) => sum + (Number(s?.totalAmount) || 0), 0);

const VendorSpendingAnalysisWidget = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const {
    inventoryReport: data,
    inventoryReportLoading: loading,
    inventoryReportError: error,
  } = useSelector((state) => state.ReportingInventory);
  const currency = useOrgCurrency();
  const [selectedVendorId, setSelectedVendorId] = useState(null);

  useEffect(() => {
    if (!data && !loading && !error) dispatch(fetchInventoryReport());
  }, [dispatch, data, loading, error]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInventoryReport());
  }, [dispatch]);

  // One row per vendor that actually has spend. A vendor can be *linked* to assets/contracts/
  // parts that carry no recorded cost — counting those (totalCount) rendered invisible zero-height
  // bars, so presence is keyed on totalAmount > 0 instead. A vendor with all-zero spend is dropped.
  const allRows = useMemo(() => {
    const rows = data?.vendorAnalyses || [];
    return rows
      .map((v) => ({
        vendorId: v.vendorId,
        vendor: v.vendorName,
        assets: sumAmount(v.assetSummaries),
        contracts: sumAmount(v.contractSummaries),
        spareParts: sumAmount(v.sparePartSummaries),
      }))
      .filter((r) => (r.assets + r.contracts + r.spareParts) > 0);
  }, [data]);

  // Resolve the selection against the live rows so a stale pick (e.g. after a refresh drops a
  // vendor) silently reverts to "All vendors" rather than showing an empty filtered chart.
  const effectiveSelectedId =
    selectedVendorId != null && allRows.some((r) => r.vendorId === selectedVendorId)
      ? selectedVendorId
      : null;

  const chartData = useMemo(() => (
    effectiveSelectedId != null
      ? allRows.filter((r) => r.vendorId === effectiveSelectedId)
      : allRows.slice(0, TOP_N)
  ), [allRows, effectiveSelectedId]);

  const allOption = useMemo(
    () => ({ value: null, label: t('shared.all_vendors', 'All Vendors') }),
    [t]
  );
  const vendorOptions = useMemo(
    () => [allOption, ...allRows.map((r) => ({ value: r.vendorId, label: r.vendor }))],
    [allOption, allRows]
  );
  const selectedOption =
    vendorOptions.find((o) => o.value === effectiveSelectedId) || allOption;

  const isEmpty = allRows.length === 0;

  return (
    <ReportWidget
      title={t('business.inventory.vendor_spending_analysis', 'Vendor Spending Analysis')}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={t('reporting.vendor_spending_analysis.empty', 'No vendor spending to display.')}
      onRefresh={handleRefresh}
    >
      <div
        style={{ maxWidth: 280, marginLeft: 'auto', marginBottom: 12 }}
        aria-label={t('shared.vendor', 'Vendor')}
      >
        <Select
          inputId="vendor-spending-filter"
          classNamePrefix="vendor-spending-filter"
          options={vendorOptions}
          value={selectedOption}
          onChange={(opt) => setSelectedVendorId(opt?.value ?? null)}
          isSearchable
          placeholder={t('shared.vendor', 'Vendor')}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        />
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="vendor" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={100} />
          <YAxis
            tick={{ fontSize: 12 }}
            tickFormatter={(v) => formatCompactMoney(v, currency, i18n.language)}
            width={80}
          />
          <Tooltip
            formatter={(value, name) => [formatMoney(value, currency), name]}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: 12,
            }}
          />
          <Legend />
          <Bar dataKey="assets" stackId="a" fill="#3b82f6" name={t('shared.assets', 'Assets')} />
          <Bar dataKey="contracts" stackId="a" fill="#10b981" name={t('shared.contracts', 'Contracts')} />
          <Bar dataKey="spareParts" stackId="a" fill="#f59e0b" name={t('shared.spare_parts', 'Spare Parts')} />
        </BarChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default VendorSpendingAnalysisWidget;
